import * as React from 'react';

import { createStyles, Theme } from '@material-ui/core';
import { fade } from '@material-ui/core/styles/colorManipulator';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import clsx from 'clsx';

function capitalize(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const styles = (theme: Theme) =>
  createStyles({
    /* Styles applied to the root element. */
    root: {
      display: 'inline-flex',
      borderRadius: theme.shape.borderRadius,
    },
    /* Styles applied to the root element if `variant="contained"`. */
    contained: {
      boxShadow: theme.shadows[2],
    },
    /* Styles applied to the root element if `disableElevation={true}`. */
    // disableElevation: {
    //   boxShadow: 'none',
    // },
    /* Pseudo-class applied to child elements if `disabled={true}`. */
    disabled: {},
    /* Styles applied to the root element if `fullWidth={true}`. */
    fullWidth: {
      width: '100%',
    },
    /* Styles applied to the root element if `orientation="vertical"`. */
    vertical: {
      flexDirection: 'column',
    },
    /* Styles applied to the children. */
    grouped: {
      minWidth: 40,
    },
    /* Styles applied to the children if `orientation="horizontal"`. */
    groupedHorizontal: {
      '&:not(:first-child)': {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      },
      '&:not(:last-child)': {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
    },
    /* Styles applied to the children if `orientation="vertical"`. */
    groupedVertical: {
      '&:not(:first-child)': {
        borderTopRightRadius: 0,
        borderTopLeftRadius: 0,
      },
      '&:not(:last-child)': {
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 0,
      },
    },
    /* Styles applied to the children if `variant="text"`. */
    groupedText: {},
    /* Styles applied to the children if `variant="text"` and `orientation="horizontal"`. */
    groupedTextHorizontal: {
      '&:not(:last-child)': {
        borderRight: `1px solid ${
          theme.palette.type === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)'
        }`,
      },
    },
    /* Styles applied to the children if `variant="text"` and `orientation="vertical"`. */
    groupedTextVertical: {
      '&:not(:last-child)': {
        borderBottom: `1px solid ${
          theme.palette.type === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)'
        }`,
      },
    },
    /* Styles applied to the children if `variant="text"` and `color="primary"`. */
    groupedTextPrimary: {
      '&:not(:last-child)': {
        borderColor: fade(theme.palette.text.primary, 0.5),
      },
    },
    /* Styles applied to the children if `variant="text"` and `color="secondary"`. */
    groupedTextSecondary: {
      '&:not(:last-child)': {
        borderColor: fade(theme.palette.secondary.main, 0.5),
      },
    },
    /* Styles applied to the children if `variant="outlined"`. */
    groupedOutlined: {},
    /* Styles applied to the children if `variant="outlined"` and `orientation="horizontal"`. */
    groupedOutlinedHorizontal: {
      '&:not(:first-child)': {
        marginLeft: -1,
      },
      '&:not(:last-child)': {
        borderRightColor: 'transparent',
      },
    },
    /* Styles applied to the children if `variant="outlined"` and `orientation="vertical"`. */
    groupedOutlinedVertical: {
      '&:not(:first-child)': {
        marginTop: -1,
      },
      '&:not(:last-child)': {
        borderBottomColor: 'transparent',
      },
    },
    /* Styles applied to the children if `variant="outlined"` and `color="primary"`. */
    groupedOutlinedPrimary: {
      '&:hover': {
        borderColor: theme.palette.text.primary,
      },
    },
    /* Styles applied to the children if `variant="outlined"` and `color="secondary"`. */
    groupedOutlinedSecondary: {
      '&:hover': {
        borderColor: theme.palette.secondary.main,
      },
    },
    /* Styles applied to the children if `variant="contained"`. */
    groupedContained: {
      boxShadow: 'none',
    },
    /* Styles applied to the children if `variant="contained"` and `orientation="horizontal"`. */
    groupedContainedHorizontal: {
      '&:not(:last-child)': {
        borderRight: `1px solid ${theme.palette.grey[400]}`,
        '&$disabled': {
          borderRight: `1px solid ${theme.palette.action.disabled}`,
        },
      },
    },
    /* Styles applied to the children if `variant="contained"` and `orientation="vertical"`. */
    groupedContainedVertical: {
      '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.grey[400]}`,
        '&$disabled': {
          borderBottom: `1px solid ${theme.palette.action.disabled}`,
        },
      },
    },
    /* Styles applied to the children if `variant="contained"` and `color="primary"`. */
    groupedContainedPrimary: {
      '&:not(:last-child)': {
        borderColor: theme.palette.primary.dark,
      },
    },
    /* Styles applied to the children if `variant="contained"` and `color="secondary"`. */
    groupedContainedSecondary: {
      '&:not(:last-child)': {
        borderColor: theme.palette.secondary.dark,
      },
    },
  });

export interface MuiButtonGroupProps extends WithStyles<typeof styles> {
  className?: string;
  color?: 'default' | 'inherit' | 'primary' | 'secondary';
  component?: any;
  disabled?: boolean;
  // disableElevation?: boolean;
  disableFocusRipple?: boolean;
  disableRipple?: boolean;
  fullWidth?: boolean;
  orientation?: 'horizontal' | 'vertical';
  size?: 'large' | 'medium' | 'small';
  variant?: 'contained' | 'outlined' | 'text';
}

/**
 * Copied from MUI until we can upgrade the package
 * https://github.com/mui-org/material-ui/blob/master/packages/material-ui/src/ButtonGroup/ButtonGroup.js
 *
 * @param props
 */
const MuiButtonGroup: React.FC<MuiButtonGroupProps> = ({
  children,
  classes,
  className,
  color = 'default',
  component: Component = 'div',
  disabled = false,
  // disableElevation = false,
  disableFocusRipple = false,
  disableRipple = false,
  fullWidth = false,
  orientation = 'horizontal',
  size = 'medium',
  variant = 'outlined',
  ...other
}) => {
  const buttonClassName = clsx(
    classes.grouped,
    // @ts-ignore
    classes[`grouped${capitalize(orientation)}`],
    // @ts-ignore
    classes[`grouped${capitalize(variant)}`],
    // @ts-ignore
    classes[`grouped${capitalize(variant)}${capitalize(orientation)}`],
    // @ts-ignore
    classes[`grouped${capitalize(variant)}${color !== 'default' ? capitalize(color) : ''}`],
    {
      [classes.disabled]: disabled,
    },
  );

  return (
    <Component
      role="group"
      className={clsx(
        classes.root,
        {
          [classes.contained]: variant === 'contained',
          [classes.vertical]: orientation === 'vertical',
          [classes.fullWidth]: fullWidth,
          // [classes.disableElevation]: disableElevation,
        },
        className,
      )}
      {...other}
    >
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
          return null;
        }

        return React.cloneElement(child, {
          className: clsx(buttonClassName, child.props.className),
          color: child.props.color || color,
          disabled: child.props.disabled || disabled,
          // disableElevation: child.props.disableElevation || disableElevation,
          disableFocusRipple,
          disableRipple,
          fullWidth,
          size: child.props.size || size,
          variant: child.props.variant || variant,
        });
      })}
    </Component>
  );
};

export default withStyles(styles, { name: 'MuiButtonGroup' })(MuiButtonGroup);
